<template>
  <div class="default__container">
    <v-app-bar app clipped-right elevation="0">
      <ui-button
        icon="icon-menu"
        icon-size="22"
        shadow
        @click.stop="sidebarShow = !sidebarShow"
      ></ui-button>
      <CompanySelect v-if="companies.length" />
      <v-spacer></v-spacer>
      <v-tooltip v-if="isFreeTrial" bottom>
        <template v-slot:activator="{ on }">
          <v-chip
            v-on="on"
            class="free-trial-chip"
            :color="subscriptionExpiresIn < 6 ? 'red' : 'warning'"
            outlined
            @click="goToSubscriptions"
          >
            <v-icon left>
              mdi-clock-alert-outline
            </v-icon>
            <div class="free-trial-chip__content">
              <span>{{ $t("message.payments.freeTrial") }}</span>
              <div>
                {{ $t("message.payments.daysLeft", [subscriptionExpiresIn]) }}
              </div>
            </div>
          </v-chip>
        </template>
        <span>
          {{ $t("message.payments.freeTrialTooltip") }}
        </span>
      </v-tooltip>
      <v-tooltip bottom>
        <template v-slot:activator="{ on }">
          <v-btn
            class="navbar-btn"
            height="40"
            outlined
            icon
            rounded
            color="text"
            v-on="on"
            @click="goToFaq"
          >
            <v-icon size="21px">mdi-help</v-icon>
          </v-btn>
        </template>
        <span>
          {{ $t("message.common.faq") }}
        </span>
      </v-tooltip>
      <ChooseLanguage class="ml-4" />
      <User />
    </v-app-bar>

    <Sidebar v-model="sidebarShow" />

    <v-main class="fill-height pb-10">
      <ExternalChangesInfoBar
        v-if="externalChangesCount && canProtectLocationData"
      />
      <div class="default__container fill-height">
        <router-view :key="`content_${keyId}`" v-if="keyId"></router-view>
        <SectionLoader :status="true" :opacity="1" v-else />
      </div>
    </v-main>
  </div>
</template>

<script>
import Sidebar from "@/components/Sidebar/Sidebar.vue";
import ChooseLanguage from "@/components/AppBar/ChooseLanguage.vue";
import User from "@/components/AppBar/User.vue";
import CompanySelect from "@/components/Permissions/CompanySelect";
import { mapGetters, mapState } from "vuex";
import SectionLoader from "@/components/Loader/SectionLoader";
import { permissionCheck } from "@/helpers/permission-check";
import ExternalChangesInfoBar from "@/components/ExternalChanges/ExternalChangesInfoBar";
import { GTM_EVENT_NAME } from "@/enums/gtm-events";
import constants from "@/config/constants";
import Intercom from "@intercom/messenger-js-sdk";
import { isFeatureFlagEnabled, FEATURE_FLAG } from "@/helpers/posthog";
import usersService from "@/services/users";
import serviceHandler from "@/helpers/handlers/service";

import { PLAN_RESTRICTIONS } from "@/enums/plan-restrictions";
import { planRestrictionsCheck } from "@/helpers/plan-restrictions-check";
import { intercomMixin } from "@/helpers/mixins/intercom";
export default {
  name: "DefaultLayout",
  mixins: [intercomMixin],
  components: {
    Sidebar,
    ChooseLanguage,
    User,
    CompanySelect,
    SectionLoader,
    ExternalChangesInfoBar
  },
  props: {
    source: String
  },
  computed: {
    ...mapState({
      companies: state => state.company.companies,
      company: state => state.company.company,
      selectedLocale: state => state.settings.selectedLocale
    }),
    ...mapGetters({
      externalChangesCount: "externalChanges/changesCount",
      isFreeTrial: "auth/isFreeTrial",
      subscriptionExpiresIn: "auth/subscriptionExpiresIn"
    }),

    keyId() {
      return this.company && this.company.id
        ? `${this.company.id}-${this.selectedLocale}`
        : null;
    },
    canProtectLocationData() {
      return planRestrictionsCheck(PLAN_RESTRICTIONS.CAN_PROTECT_LOCATION_DATA);
    },
    currentLocale() {
      return this.$i18n.locale;
    },
    faqLink() {
      return constants.faq[this.currentLocale];
    }
  },
  data: () => ({
    sidebarShow: true
  }),
  watch: {
    company(company) {
      if (!company) return;
      if (
        this.$route.meta &&
        this.$route.meta.permission &&
        !permissionCheck(this.$route.meta.permission)
      ) {
        this.$router.push({
          name: "dashboard"
        });
      }
    }
  },
  created() {
    this.initGtmSession();
  },
  methods: {
    goToSubscriptions() {
      this.$gtm.trackCustomEvent({
        event: GTM_EVENT_NAME.FREE_TRIAL_REMINDER_CLICKED
      });
      this.$router.push({ name: "subscription-settings" });
    },
    async initGtmSession() {
      const userAuthEmail = this.$store.state.auth.user.email;

      const {
        id: company_id,
        is_paying: company_is_paying,
        is_testing: company_is_testing,
        email: user_email
      } = this.$store.state.company.company;

      const { user_id } = this.$store.state.company.company.company_user;

      window.dataLayer.push({
        user_id
      });

      if (isFeatureFlagEnabled(FEATURE_FLAG.CN_5810_INTERCOM_INTEGRATION)) {
        await this.setIntercom();
      } else {
        window.civchat = {
          ...window.civchat,
          user_id,
          company_id,
          email: userAuthEmail
        };
      }

      this.$gtm.trackCustomEvent({
        event: GTM_EVENT_NAME.APP_INITIALIZE
      });

      this.$gtm.trackCustomEvent({
        event: GTM_EVENT_NAME.SET_USER_DATA,
        user_id,
        user_email,
        company_id,
        company_is_paying,
        company_is_testing
      });
    },
    goToFaq() {
      this.$gtm.trackCustomEvent({
        event: GTM_EVENT_NAME.SHOW_FAQ
      });
      window.open(this.faqLink, "_blank");
    }
  }
};
</script>
<style lang="scss" scoped>
.navbar-btn {
  border: 1px solid rgba(54, 60, 86, 0.08);
}
.free-trial-chip {
  height: 40px;
  display: flex;
  gap: $spacing * 2;
  border-radius: 50px;

  .free-trial-chip__content {
    display: flex;
    align-items: flex-start;
    flex-direction: column;
    span {
      @include font-size(12px);
      line-height: 1em;
    }
    div {
      line-height: 1rem;
    }
  }
}
</style>
